// Enclosure labels: lightweight DOM tags over enclosures showing their animals.
// Content recomputes only on 'animals:changed' (event-driven, not per frame). Each throttled
// tick we just project the nearest on-screen enclosure centers and position pooled divs.
import * as THREE from 'three';
import { bus } from '../core/bus.js';
import { SPECIES } from './animalCatalog.js';

export class EnclosureLabels {
  constructor(world) {
    this.world = world;
    this.root = document.createElement('div');
    this.root.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:5;overflow:hidden';
    document.body.appendChild(this.root);
    this.pool = [];
    this.data = [];
    this._t = 0;
    this._v = new THREE.Vector3();
    // Labels don't need to be instant: refresh the (DOM-building) summary ~2s (≈120 frames @60fps)
    // after animal changes settle, so a busy day-roll never hitches on label rebuilds.
    this._subs = [bus.on('animals:changed', () => {
      clearTimeout(this._refreshT);
      this._refreshT = setTimeout(() => this.refresh(), 2000);
    })];
    this.refresh();
  }
  dispose() { this._subs.forEach((u) => u()); this.root.remove(); }

  _div() {
    const d = document.createElement('div');
    d.style.cssText = 'position:absolute;transform:translate(-50%,-100%);background:rgba(20,28,36,.72);color:#fff;font:600 12px system-ui;padding:3px 8px;border-radius:8px;white-space:nowrap;backdrop-filter:blur(4px);box-shadow:0 2px 8px rgba(0,0,0,.25)';
    this.root.appendChild(d);
    return d;
  }

  // Rebuild per-enclosure summary + cached world-space center. Cheap; runs only on change.
  refresh() {
    const w = this.world, t = w.terrain, half = t.worldSize / 2;
    const byEnc = new Map();
    for (const a of w.animals.animals) {
      if (a.escaped) continue;
      let m = byEnc.get(a.enclosureId); if (!m) { m = new Map(); byEnc.set(a.enclosureId, m); }
      m.set(a.species, (m.get(a.species) || 0) + 1);
    }
    this.data = [];
    for (const e of w.build.enclosures) {
      const m = byEnc.get(e.id); if (!m || !e.cells.size) continue;
      let sx = 0, sz = 0, n = 0;
      for (const key of e.cells) { const [cx, cz] = key.split(',').map(Number); sx += cx; sz += cz; n++; }
      const wx = (sx / n + 0.5) * t.cellSize - half, wz = (sz / n + 0.5) * t.cellSize - half;
      const center = new THREE.Vector3(wx, t.heightAtWorld(wx, wz) + 3, wz);
      const parts = [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3)
        .map(([sp, c]) => `${SPECIES[sp] ? SPECIES[sp].label : sp} \u00d7${c}`);
      this.data.push({ center, text: parts.join(' \u00b7 ') });
    }
  }

  update() {
    this._t++; if (this._t % 8 !== 0) return;
    const cam = this.world.renderer.camera, dom = this.world.renderer.renderer.domElement;
    const rect = dom.getBoundingClientRect();
    const cand = [];
    for (const d of this.data) {
      const dist = cam.position.distanceTo(d.center);
      if (dist > 220) continue;
      this._v.copy(d.center).project(cam);
      if (this._v.z > 1 || this._v.x < -1 || this._v.x > 1 || this._v.y < -1 || this._v.y > 1) continue;
      cand.push({ d, dist, x: rect.left + (this._v.x * 0.5 + 0.5) * rect.width, y: rect.top + (-this._v.y * 0.5 + 0.5) * rect.height });
    }
    cand.sort((a, b) => a.dist - b.dist);
    if (cand.length > 20) cand.length = 20;
    for (let i = 0; i < cand.length; i++) {
      let el = this.pool[i]; if (!el) { el = this._div(); this.pool.push(el); }
      el.style.display = 'block';
      el.textContent = cand[i].d.text;
      el.style.left = Math.round(cand[i].x) + 'px';
      el.style.top = Math.round(cand[i].y) + 'px';
    }
    for (let i = cand.length; i < this.pool.length; i++) this.pool[i].style.display = 'none';
  }
}
